/**
 * Brand asset manifest. Web imports the files through its bundler
 * (`new URL('@echo/brand/assets/logo-lockup.png', import.meta.url)` or a
 * static import); native uses `require` via `@echo/brand/native`.
 * Source: Figma "Stronger Memory" — logo `2052:2` (lockup, 1504×648), the
 * mascot poses from the `illustration/brian` component set (the "brain guy"
 * style, 1254×1254, transparent; support, offline and the avatar
 * still use the earlier style until theirs are drawn), streak logo mark
 * `› logo` (3300×2336, transparent).
 */
export const brandAssets = {
  logoLockup: {
    file: 'logo-lockup.png',
    width: 1504,
    height: 648,
    figmaNode: '2052:2',
  },
  /** Sign-in lockup: Stronger Memory beside "Powered by Goodwin Living Foundation". */
  logoLockupGoodwin: {
    file: 'logo-lockup-goodwin.png',
    width: 2300,
    height: 600,
    figmaNode: '2817:60614',
  },
  /** Brian with his open book — the reading completion (illustration/brian › reading, brain guy). */
  mascotExerciseComplete: {
    file: 'mascot-exercise-complete.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17941',
  },
  /** Logo mark cropped for the streak card corner; drawn at 127×90. */
  streakLogoMark: {
    file: 'streak-logo-mark.png',
    width: 3300,
    height: 2336,
    figmaNode: '2074:3456',
  },
  /** Brian waving on the welcome screen after sign-in (illustration/brian › welcome, brain guy). */
  mascotWelcome: {
    file: 'mascot-welcome.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17949',
  },
  /** Rob Liebreich, CEO of Goodwin Living, on the About screen. */
  portraitRobLiebreich: {
    file: 'portrait-rob-liebreich.png',
    width: 250,
    height: 250,
    figmaNode: '2817:60629',
  },
  /**
   * Home's "all done" card. The brain-guy style has no celebrate pose yet, so this is its
   * welcome pose, arms up (illustration/brian › welcome) — swap the file when one is drawn.
   */
  mascotCelebrates: {
    file: 'mascot-celebrates.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17949',
  },
  /** Brian pointing at a group card with a plus (illustration/brian › groups) — My groups, empty. */
  mascotGroups: {
    file: 'mascot-groups.png',
    width: 1254,
    height: 1254,
    figmaNode: '3662:33800',
  },
  /** Brian in a headset holding a speech bubble with a heart — Help & Support. */
  mascotSupport: {
    file: 'mascot-support.png',
    width: 1254,
    height: 1254,
    figmaNode: '2831:112711',
  },
  /** Brian waving beside an open door — "Leave this group?" (group/options › Leave group). */
  mascotLeaveGroup: {
    file: 'mascot-leave-group.png',
    width: 1374,
    height: 1145,
    figmaNode: 'I3951:181278;992:18137;156:6199',
  },
  /** Brian, worried, holding a red flag — "Report this group" (group/options › Report group). */
  mascotReportGroup: {
    file: 'mascot-report-group.png',
    width: 1374,
    height: 1145,
    figmaNode: 'I3951:214038;992:18944;156:6199',
  },
  /** The character celebrating with a stopwatch and a check — "You did it!", the countdown ran out. */
  mascotTimeUp: { file: 'mascot-time-up.png', width: 1254, height: 1254, figmaNode: '3371:43484' },
  /** The character, worried, holding a stopwatch in the red — "Don't leave yet", Done with time left. */
  mascotNotYet: {
    file: 'mascot-not-yet.png',
    width: 1254,
    height: 1254,
    figmaNode: '3408:121589',
  },
  /** Brian, worried, holding a crossed-out Wi-Fi sign — "Oh! You're offline". */
  mascotOffline: { file: 'mascot-offline.png', width: 1254, height: 1254, figmaNode: '2817:60265' },
  /** Brian holding a bell — "Notifications are off" (illustration/brian › notifications, brain guy). */
  mascotNotifications: {
    file: 'mascot-notifications.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17948',
  },
  /** Brian with a 404 sign — the web's "Let's get you back on track" (illustration/brian › 404-error). */
  mascotLost: {
    file: 'mascot-lost.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17944',
  },
  /** Brian with a phone — "Allow camera access" (illustration/brian › camera-access, brain guy). */
  mascotCamera: {
    file: 'mascot-camera.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17947',
  },
  /** Brian with a stack of photos — "Allow photo access" (illustration/brian › photo-access, brain guy). */
  mascotPhotos: {
    file: 'mascot-photos.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17943',
  },
  /**
   * The coach on every exercise intro: Brian with a plain stopwatch — no "10:00" on it, since
   * the duration is the person's own choice (illustration/brian › timer, brain guy).
   */
  mascotIntroTimer: {
    file: 'mascot-intro-timer.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17945',
  },
  // Per-exercise completion illustrations (reading keeps mascot-exercise-complete).
  /** Brian writing in a notepad — the writing read-back and completion (illustration/brian › writing). */
  mascotCompleteWriting: {
    file: 'mascot-complete-writing.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17942',
  },
  /** Brian beside a ticked sum — the Quick math completion (illustration/brian › math, brain guy). */
  mascotCompleteMath: {
    file: 'mascot-complete-math.png',
    width: 1254,
    height: 1254,
    figmaNode: '3219:17946',
  },
} as const;

export type BrandAssetName = keyof typeof brandAssets;

export * from './chat-vectors';
export { groupVectors } from './group-vectors';
export * from './vectors';
